<script lang="ts">
	// UC-12, the owner's half: today's orders across the shops, with one
	// summary line per shop. Read-only on purpose: a void happens on the till,
	// where the money is.
	//
	// "Today" is each shop's trading day, which starts at its own hour.
	//
	// An order's details name the discount it was given, what it took and who
	// gave it (UC-35): the cashier who placed the order.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { fill, message, type Language, type MessageKey } from '../i18n/messages';
	import { discountLabel } from '../format/discount';
	import { formatMinor } from '../format/money';
	import { orderNumber, payLine } from '../format/order';
	import { formatClock } from '../format/time';
	import type { Account } from '../owner/session.svelte';
	import {
		loadDays,
		ordersShopFilter,
		ownerShops,
		ownerDays,
		setOrdersShop,
	} from '../owner/data.svelte';
	import type { components } from '../api/types';

	type Order = components['schemas']['OrderBody'];

	interface Props {
		language: Language;
		account: Account;
		shopId?: string;
	}

	let { language, account, shopId = '' }: Props = $props();

	const minorDigits = $derived(account.settings.minorDigits);
	const shops = $derived(ownerShops());
	const days = $derived(ownerDays());
	const filter = $derived(shopId || ordersShopFilter());

	$effect(() => {
		if (shops.loaded) void loadDays();
	});

	let detail = $state('');

	const shown = $derived(shops.value.filter((shop) => filter === 'all' || shop.id === filter));

	const rows = $derived(
		shown
			.flatMap((shop) =>
				(days.days[shop.id]?.orders ?? []).map((order) => ({ order, shop: shop.name })),
			)
			.sort((a, b) => (b.order.placedAt ?? '').localeCompare(a.order.placedAt ?? '')),
	);

	const types: Record<string, MessageKey> = {
		dine_in: 'sellDineIn',
		takeaway: 'sellTakeaway',
		delivery: 'sellDeliveryType',
	};

	// First-strong isolate and its end, by code point: a table's name keeps
	// its own direction inside the other language's line (UC-39).
	const ISOLATE = String.fromCodePoint(0x2068);
	const POP_ISOLATE = String.fromCodePoint(0x2069);

	// The order's type, and for a dine-in at a table the table as the order
	// kept it: "Dine in · Table T5".
	function typeOf(order: Order): string {
		const type = message(language, types[order.type] ?? 'sellDineIn');
		if (!order.tableName) return type;
		const table = fill(message(language, 'tableLabel'), ISOLATE + order.tableName + POP_ISOLATE);
		return `${type} · ${table}`;
	}

	function statusOf(order: Order): { key: MessageKey; tone: 'ok' | 'flag' | 'quiet' } {
		if (order.status === 'paid') return { key: 'ordersPaid', tone: 'ok' };
		if (order.status === 'void') return { key: 'ordersVoid', tone: 'quiet' };
		return { key: 'ordersOpen', tone: 'flag' };
	}

	function summaryOf(shopId: string): { key: MessageKey; value: string }[] {
		const summary = days.days[shopId]?.summary;
		return [
			{ key: 'ordersCash', value: formatMinor(summary?.cashMinor ?? 0, minorDigits) },
			{ key: 'ordersCard', value: formatMinor(summary?.cardMinor ?? 0, minorDigits) },
			{ key: 'ordersDue', value: formatMinor(summary?.dueMinor ?? 0, minorDigits) },
			{ key: 'ordersPaid', value: String(summary?.paidCount ?? 0) },
			{ key: 'ordersVoid', value: String(summary?.voidCount ?? 0) },
			{ key: 'ordersNoSale', value: String(summary?.noSaleCount ?? 0) },
		];
	}

	const note = $derived(
		shown.length === 1
			? fill(message(language, 'ordersTradingDay'), shown[0]?.dayStartHour ?? 0)
			: undefined,
	);

	const columns = $derived([
		{ label: message(language, 'ordersNumber'), w: '56px' },
		{ label: message(language, 'ordersShop'), w: 'minmax(0,1fr)' },
		{ label: message(language, 'ordersTime'), w: '92px' },
		{ label: message(language, 'ordersType'), w: 'minmax(0,.8fr)' },
		{ label: message(language, 'ordersStatus'), w: '96px' },
		{ label: message(language, 'sellTotal'), w: '104px', align: 'end' as const },
		{ label: '', w: '84px' },
	]);
</script>

<div class="orders" data-testid="panel-owner-orders">
	{#if !shopId}
		<div class="filters">
			<Segmented
				label={message(language, 'ordersShop')}
				options={[
					{ value: 'all', label: message(language, 'ordersAllShops'), testId: 'orders-shop-all' },
					...shops.value.map((shop) => ({
						value: shop.id,
						label: shop.name,
						testId: `orders-shop-${shop.id}`,
					})),
				]}
				value={filter}
				onChange={(picked) => setOrdersShop(picked)}
			/>
		</div>
	{/if}

	{#if !shops.loaded || !days.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else}
		{#each shown as shop (shop.id)}
			<div class="summary" data-testid="orders-summary-{shop.id}">
				<span class="shop">{shop.name}</span>
				{#each summaryOf(shop.id) as item (item.key)}
					<span class="stat"
						>{message(language, item.key)} <bdi class="n" dir="ltr">{item.value}</bdi></span
					>
				{/each}
			</div>
		{/each}

		{#if rows.length === 0}
			<Card pad>
				<p class="empty">{message(language, 'ordersEmpty')}</p>
			</Card>
		{:else}
			<TablePanel
				{columns}
				title={message(language, 'ordersHeading')}
				{note}
				testId="owner-orders-list"
			>
				{#each rows as { order, shop } (order.id)}
					{@const status = statusOf(order)}
					<Row testId="owner-order-{order.id}">
						<span class="n strong" dir="ltr">{orderNumber(order)}</span>
						<span class="soft">{shop}</span>
						<span class="n soft" dir="ltr">{formatClock(order.placedAt, language)}</span>
						<span class="soft" data-testid="owner-order-type-{order.id}">{typeOf(order)}</span>
						<span class="cell">
							<Chip text={message(language, status.key)} tone={status.tone} size="md" dot />
						</span>
						<span class="n strong end" dir="ltr">{formatMinor(order.totalMinor, minorDigits)}</span>
						<span class="actions">
							<Button
								testId="owner-order-details-{order.id}"
								label={detail === order.id
									? message(language, 'ordersCloseDetails')
									: message(language, 'ordersDetails')}
								tone="text-quiet"
								size="sm"
								onPress={() => (detail = detail === order.id ? '' : order.id)}
							/>
						</span>
					</Row>
					{#if detail === order.id}
						<div class="detail">
							<div class="lines">
								{#each order.lines ?? [] as line, index (index)}
									<div class="line">
										<span
											><bdi class="n" dir="ltr">{line.qty}×</bdi>
											<!-- "Fish · 250 g", as the till names it (lineName): the
											separator sits outside the block, which would trim its spaces. -->
											{line.name}{line.grams ? ' · ' : ''}{#if line.grams}<bdi
													class="n"
													dir="ltr"
													data-testid="owner-order-grams">{line.grams}</bdi
												>
												{message(language, 'unitGrams')}{/if}
											<span class="muted"
												>{(line.modifiers ?? []).map((m) => m.name).join(' · ')}</span
											></span
										>
										<span class="n" dir="ltr">{formatMinor(line.lineTotalMinor, minorDigits)}</span>
									</div>
								{/each}
								{#if order.discount}
									<div class="line given" data-testid="owner-order-discount-{order.id}">
										<span
											>{fill(
												message(language, 'ordersDiscountGiven'),
												discountLabel(language, order.discount, minorDigits),
												order.discountGiverName ?? order.cashierName ?? '',
											)}</span
										>
										<span class="n" dir="ltr">{formatMinor(-order.discountMinor, minorDigits)}</span
										>
									</div>
								{/if}
							</div>
							<div class="facts">
								<span>{order.cashierName ?? ''} · {payLine(language, order, minorDigits)}</span>
								{#if order.customerName || order.deliveryAddress}
									<span
										>{[order.customerName, order.deliveryAddress].filter(Boolean).join(' · ')}</span
									>
								{/if}
								{#if order.voidReason}
									<span>{message(language, 'ordersVoidReason')} {order.voidReason}</span>
								{/if}
								{#if order.payment && !order.shiftId}
									<span data-testid="owner-order-no-shift-{order.id}"
										>{message(language, 'shiftsNoShift')}</span
									>
								{/if}
							</div>
						</div>
					{/if}
				{/each}
			</TablePanel>
		{/if}
		<p class="foot">{message(language, 'ordersTillOnly')}</p>
	{/if}
</div>

<style>
	.orders {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.filters {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.summary {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: var(--space-2) var(--space-7);
		padding: var(--space-5) var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
	}

	.shop {
		font-size: var(--text-sm);
		font-weight: 700;
		min-inline-size: 110px;
	}

	.stat {
		white-space: nowrap;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.stat .n {
		font-size: var(--text-sm);
		font-weight: 700;
		color: var(--text-strong);
	}

	.n {
		font-family: var(--font-mono);
	}

	.strong {
		font-weight: 700;
	}

	.soft {
		min-inline-size: 0;
		color: var(--text-body);
		text-align: start;
	}

	.cell {
		min-inline-size: 0;
		display: flex;
	}

	.end {
		min-inline-size: 0;
		text-align: end;
	}

	.actions {
		min-inline-size: 0;
		display: flex;
		justify-content: flex-end;
	}

	.empty {
		margin: 0;
		font-size: var(--text-md);
		color: var(--text-body);
	}

	.detail {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
		gap: var(--space-7);
		padding: var(--row-pad);
		background: var(--surface-card-header);
		border-block-end: var(--border-width) solid var(--border-inset);
		font-size: var(--text-sm);
	}

	.lines,
	.facts {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.facts {
		color: var(--text-body);
	}

	.line {
		display: flex;
		gap: var(--space-4);
		justify-content: space-between;
	}

	.muted {
		color: var(--text-muted);
	}

	.given {
		padding-block-start: var(--space-2);
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.foot {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}
</style>
